import { UserError } from '../../errors';
import type { ToolOutputImage } from '../../types/protocol';

export type ImageDimensions = {
  width: number;
  height: number;
  pixelCount: number;
};

export type ViewImageMetadata = ImageDimensions & {
  kind: 'view_image';
  path: string;
  mimeType: string;
  byteSize: number;
  estimatedImageTokens: number;
};

export const DEFAULT_VIEW_IMAGE_MAX_WIDTH = 8_000;
export const DEFAULT_VIEW_IMAGE_MAX_HEIGHT = 8_000;
export const DEFAULT_VIEW_IMAGE_MAX_PIXELS = 64_000_000;
export const DEFAULT_VIEW_IMAGE_MAX_BYTES = 32 * 1024 * 1024;

function mediaTypeFromPath(path?: string): string | null {
  const lowerPath = path?.trim().toLowerCase() ?? '';
  if (lowerPath.endsWith('.png')) {
    return 'image/png';
  }
  if (lowerPath.endsWith('.jpg') || lowerPath.endsWith('.jpeg')) {
    return 'image/jpeg';
  }
  if (lowerPath.endsWith('.gif')) {
    return 'image/gif';
  }
  if (lowerPath.endsWith('.webp')) {
    return 'image/webp';
  }
  if (lowerPath.endsWith('.bmp')) {
    return 'image/bmp';
  }
  if (lowerPath.endsWith('.tif') || lowerPath.endsWith('.tiff')) {
    return 'image/tiff';
  }
  if (lowerPath.endsWith('.svg') || lowerPath.endsWith('.svgz')) {
    return 'image/svg+xml';
  }
  return null;
}

export function sniffImageMediaType(
  bytes: Uint8Array,
  path?: string,
): string | null {
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38
  ) {
    return 'image/gif';
  }
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return 'image/bmp';
  }
  if (
    (bytes[0] === 0x49 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x2a &&
      bytes[3] === 0x00) ||
    (bytes[0] === 0x4d &&
      bytes[1] === 0x4d &&
      bytes[2] === 0x00 &&
      bytes[3] === 0x2a)
  ) {
    return 'image/tiff';
  }
  if (looksLikeSvg(bytes)) {
    return 'image/svg+xml';
  }
  return mediaTypeFromPath(path);
}

function looksLikeSvg(bytes: Uint8Array): boolean {
  const prefix = new TextDecoder()
    .decode(bytes.subarray(0, Math.min(bytes.byteLength, 512)))
    .trimStart()
    .toLowerCase();
  return prefix.startsWith('<svg') || /^<\?xml[\s\S]*<svg/u.test(prefix);
}

export function sniffImageDimensions(
  bytes: Uint8Array,
  mediaType: string,
): ImageDimensions | null {
  switch (mediaType) {
    case 'image/png':
      return sniffPngDimensions(bytes);
    case 'image/jpeg':
      return sniffJpegDimensions(bytes);
    case 'image/gif':
      return sniffGifDimensions(bytes);
    case 'image/webp':
      return sniffWebpDimensions(bytes);
    default:
      return null;
  }
}

export function imageOutputFromBytes(
  path: string,
  bytes: Uint8Array,
  options: { maxBytes?: number } = {},
): ToolOutputImage {
  assertViewImageByteLength(path, bytes.byteLength, options.maxBytes);
  const mediaType = sniffImageMediaType(bytes, path);
  if (!mediaType) {
    throw new UserError(`Unsupported image format for view_image: ${path}`);
  }
  const dimensions = sniffImageDimensions(bytes, mediaType);
  const providerData = dimensions
    ? buildViewImageMetadata(path, mediaType, bytes.byteLength, dimensions)
    : {
        kind: 'view_image' as const,
        path,
        mimeType: mediaType,
        byteSize: bytes.byteLength,
      };
  return {
    type: 'image',
    image: {
      data: Uint8Array.from(bytes),
      mediaType,
    },
    providerData,
  };
}

export function assertViewImageByteLength(
  path: string,
  byteLength: number,
  maxBytes = DEFAULT_VIEW_IMAGE_MAX_BYTES,
) {
  const limit =
    Number.isFinite(maxBytes) && maxBytes > 0
      ? maxBytes
      : DEFAULT_VIEW_IMAGE_MAX_BYTES;
  if (byteLength > limit) {
    throw new UserError(
      `Image file exceeds the view_image transfer safety limit (${limit} bytes): ${path}`,
    );
  }
}

export function buildViewImageMetadata(
  path: string,
  mimeType: string,
  byteSize: number,
  dimensions: ImageDimensions,
): ViewImageMetadata {
  return {
    kind: 'view_image',
    path,
    mimeType,
    byteSize,
    ...dimensions,
    estimatedImageTokens: estimateImageTokensForPixels(dimensions.pixelCount),
  };
}

export function estimateImageTokensForPixels(pixelCount: number): number {
  if (pixelCount <= 1_000_000) {
    return 1_000;
  }
  if (pixelCount <= 4_000_000) {
    return 2_000;
  }
  if (pixelCount <= 16_000_000) {
    return 4_000;
  }
  return 8_000;
}

function sniffPngDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (
    bytes.length < 24 ||
    bytes[0] !== 0x89 ||
    bytes[1] !== 0x50 ||
    bytes[2] !== 0x4e ||
    bytes[3] !== 0x47 ||
    bytes[12] !== 0x49 ||
    bytes[13] !== 0x48 ||
    bytes[14] !== 0x44 ||
    bytes[15] !== 0x52
  ) {
    return null;
  }
  return dimensionsFromWidthHeight(
    readUInt32BE(bytes, 16),
    readUInt32BE(bytes, 20),
  );
}

function sniffGifDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (
    bytes.length < 10 ||
    bytes[0] !== 0x47 ||
    bytes[1] !== 0x49 ||
    bytes[2] !== 0x46 ||
    bytes[3] !== 0x38
  ) {
    return null;
  }
  return dimensionsFromWidthHeight(
    readUInt16LE(bytes, 6),
    readUInt16LE(bytes, 8),
  );
}

function sniffWebpDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (
    bytes.length < 30 ||
    bytes[0] !== 0x52 ||
    bytes[1] !== 0x49 ||
    bytes[2] !== 0x46 ||
    bytes[3] !== 0x46 ||
    bytes[8] !== 0x57 ||
    bytes[9] !== 0x45 ||
    bytes[10] !== 0x42 ||
    bytes[11] !== 0x50
  ) {
    return null;
  }

  const chunk = String.fromCharCode(
    bytes[12]!,
    bytes[13]!,
    bytes[14]!,
    bytes[15]!,
  );
  if (chunk === 'VP8 ') {
    if (
      bytes.length < 30 ||
      bytes[23] !== 0x9d ||
      bytes[24] !== 0x01 ||
      bytes[25] !== 0x2a
    ) {
      return null;
    }
    return dimensionsFromWidthHeight(
      readUInt16LE(bytes, 26) & 0x3fff,
      readUInt16LE(bytes, 28) & 0x3fff,
    );
  }
  if (chunk === 'VP8L') {
    if (bytes.length < 25 || bytes[20] !== 0x2f) {
      return null;
    }
    const b0 = bytes[21]!;
    const b1 = bytes[22]!;
    const b2 = bytes[23]!;
    const b3 = bytes[24]!;
    const width = 1 + (((b1 & 0x3f) << 8) | b0);
    const height = 1 + ((b3 << 6) | (b2 >> 2) | ((b1 & 0xc0) << 6));
    return dimensionsFromWidthHeight(width, height);
  }
  if (chunk === 'VP8X') {
    if (bytes.length < 30) {
      return null;
    }
    const width = 1 + readUInt24LE(bytes, 24);
    const height = 1 + readUInt24LE(bytes, 27);
    return dimensionsFromWidthHeight(width, height);
  }
  return null;
}

function sniffJpegDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return null;
  }

  let offset = 2;
  while (offset < bytes.length) {
    while (offset < bytes.length && bytes[offset] === 0xff) {
      offset += 1;
    }
    if (offset >= bytes.length) {
      return null;
    }

    const marker = bytes[offset]!;
    offset += 1;

    if (marker === 0xd9 || marker === 0xda) {
      return null;
    }
    if (isJpegStandaloneMarker(marker)) {
      continue;
    }
    if (offset + 2 > bytes.length) {
      return null;
    }

    const segmentLength = readUInt16BE(bytes, offset);
    if (segmentLength < 2 || offset + segmentLength > bytes.length) {
      return null;
    }

    if (isJpegStartOfFrameMarker(marker)) {
      if (segmentLength < 7) {
        return null;
      }
      const height = readUInt16BE(bytes, offset + 3);
      const width = readUInt16BE(bytes, offset + 5);
      return dimensionsFromWidthHeight(width, height);
    }

    offset += segmentLength;
  }

  return null;
}

function isJpegStandaloneMarker(marker: number): boolean {
  return marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7);
}

function isJpegStartOfFrameMarker(marker: number): boolean {
  return (
    marker >= 0xc0 &&
    marker <= 0xcf &&
    marker !== 0xc4 &&
    marker !== 0xc8 &&
    marker !== 0xcc
  );
}

function dimensionsFromWidthHeight(
  width: number,
  height: number,
): ImageDimensions | null {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return {
    width,
    height,
    pixelCount: width * height,
  };
}

function readUInt16BE(bytes: Uint8Array, offset: number): number {
  return (bytes[offset]! << 8) | bytes[offset + 1]!;
}

function readUInt16LE(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! | (bytes[offset + 1]! << 8);
}

function readUInt24LE(bytes: Uint8Array, offset: number): number {
  return (
    bytes[offset]! | (bytes[offset + 1]! << 8) | (bytes[offset + 2]! << 16)
  );
}

function readUInt32BE(bytes: Uint8Array, offset: number): number {
  return (
    bytes[offset]! * 0x1000000 +
    ((bytes[offset + 1]! << 16) |
      (bytes[offset + 2]! << 8) |
      bytes[offset + 3]!)
  );
}
